import { Box, Checkbox, CircularProgress, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../global/theme/tokens';
import { extractCredentialIds } from '../helpers/outOfOffice';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * The vacation driven reply, shown as a full width bar above the split rather
 * than inside the notification list: it is a standing setting, not an entry —
 * it has no period, cannot be deleted, and is the only one that can be switched
 * off. Sitting outside the list says all of that without extra chrome.
 *
 * The checkbox toggles it; clicking anywhere else opens the editor.
 */
export const VacationSourceCard = ({
  data,
  isSelected,
  isBusy,
  // The entry could not be loaded — there is nothing to switch.
  isDisabled,
  onToggle,
  onOpen,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const isEnabled = Boolean(data?.isEnabled);

  // Switched on but nothing to send with — worth pointing out before a vacation
  // starts and the reply goes out empty.
  const isIncomplete =
    isEnabled &&
    (!data?.subject || !data?.body || !extractCredentialIds(data).length);

  const accent = isEnabled ? colors.success : colors.money.neutral;
  const border = isSelected
    ? colors.info + '90'
    : isEnabled
      ? colors.success + '50'
      : 'transparent';

  return (
    <Box
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        // Space on the checkbox must only toggle — not also open the editor.
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ') onOpen();
      }}
      className="shrink-0 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg cursor-pointer mb-2"
      sx={{
        padding: '4px 10px 4px 4px',
        bgcolor: isSelected
          ? colors.info + '25'
          : isEnabled
            ? colors.success + '15'
            : colors.card,
        border: `1px solid ${border}`,
        transition: '0.2s ease',
        ':hover': {
          bgcolor: colors.info + '25',
          border: `1px solid ${colors.info}80`,
        },
        '&:hover .ooo-vacation-chevron': { opacity: 0.7 },
      }}
    >
      <Tooltip
        title={isEnabled ? t('Switch off') : t('Switch on')}
        disableInteractive
      >
        <Box
          // The checkbox toggles; it must not also open the editor.
          onClick={(event) => event.stopPropagation()}
          className="flex items-center shrink-0"
        >
          {isBusy ? (
            <Box className="flex items-center justify-center w-[34px] h-[34px]">
              <CircularProgress size="15px" color="inherit" />
            </Box>
          ) : (
            <Checkbox
              size="small"
              checked={isEnabled}
              disabled={isDisabled}
              onChange={(event) => onToggle(event.target.checked)}
              sx={{
                padding: '5px',
                color: accent,
                '&.Mui-checked': { color: colors.success },
              }}
            />
          )}
        </Box>
      </Tooltip>

      <BeachAccessIcon sx={{ fontSize: '17px', color: accent, flexShrink: 0 }} />

      <Typography fontSize={13} fontWeight={600} noWrap>
        {t('Out of office from vacations')}
      </Typography>

      <Typography fontSize={11} className="opacity-60 flex-1 min-w-0" noWrap>
        {isEnabled
          ? t('Replies automatically during your approved vacations.')
          : t('Switch on to reply automatically during your vacations.')}
      </Typography>

      {isIncomplete && (
        <Box
          className="flex items-center gap-1 rounded-full px-2 shrink-0"
          sx={{
            color: colors.warning,
            bgcolor: colors.warning + '25',
            border: `1px solid ${colors.warning}60`,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: '13px' }} />
          <Typography fontSize={10} fontWeight={700} noWrap>
            {t('Message is incomplete')}
          </Typography>
        </Box>
      )}

      <ChevronRightIcon
        className="ooo-vacation-chevron shrink-0"
        sx={{ fontSize: '17px', opacity: 0, transition: '0.2s ease' }}
      />
    </Box>
  );
};
